import { useAuth0 } from '@auth0/auth0-react';
import useSWRInfinite from 'swr/infinite';
import http from '@/Utils/HTTP/NewHttpUtils';

/**
 * Lifecycle of a job as the connected ATS reports it. `Unknown` covers a status the backend did not
 * recognise — the job is still offered rather than hidden. Nothing can be filtered by `Unknown`; see
 * {@link AtsJobStatusFilter}.
 */
export type AtsJobStatus = 'Unknown' | 'Open' | 'Closed' | 'Draft' | 'Archived';

/**
 * What to ask the ATS for. Separate from {@link AtsJobStatus} because the vocabularies genuinely
 * differ at both ends: you can ask for `All`, but no single job is ever "all".
 */
export type AtsJobStatusFilter = 'All' | 'Open' | 'Closed' | 'Draft' | 'Archived';

export interface AtsJobResponse {
    id: string;
    name: string | null;
    jobCode: string | null;
    status: AtsJobStatus;
    department: string | null;
    location: string | null;
    openedAt: string | null;
}

export interface AtsJobPageResponse {
    jobs: AtsJobResponse[];
    /** Cursor for the following page, or null on the last page. */
    nextCursor: string | null;
}

const buildPath = (companyId: string, status: AtsJobStatusFilter, nameContains: string, cursor: string | null) => {
    const query = new URLSearchParams({ status });

    if (nameContains.trim()) {
        query.set('nameContains', nameContains.trim());
    }

    if (cursor) {
        query.set('cursor', cursor);
    }

    return `company/${companyId}/ats-integration/jobs?${query.toString()}`;
};

interface UseAtsJobsParams {
    companyId: string;
    status: AtsJobStatusFilter;
    nameContains: string;
    /** Held until the import dialog opens — this call reaches out to the ATS provider. */
    enabled: boolean;
}

/**
 * The company's connected ATS jobs, loaded a page at a time and accumulated behind a "load more".
 */
const useAtsJobs = ({ companyId, status, nameContains, enabled }: UseAtsJobsParams) => {
    const { getAccessTokenSilently } = useAuth0();

    // useSWRInfinite asks for each page's key in turn, handing us the previous page so we can read its
    // forward-only cursor. Returning null holds the request until enabled, or ends the list.
    const getKey = (pageIndex: number, previousPageData: AtsJobPageResponse | null): string | null => {
        if (!enabled) {
            return null;
        }

        if (previousPageData && !previousPageData.nextCursor) {
            return null;
        }

        const cursor = pageIndex === 0 ? null : (previousPageData?.nextCursor ?? null);

        return buildPath(companyId, status, nameContains, cursor);
    };

    return useSWRInfinite<AtsJobPageResponse>(
        getKey,
        async (path) => {
            return http.get<AtsJobPageResponse>({
                path,
                accessToken: await getAccessTokenSilently(),
            });
        },
        {
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            revalidateFirstPage: false,
        },
    );
};

export default useAtsJobs;
